import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { ImportantNote, KeyTerm } from "../note-components";
import type { UnitNotes } from "../types";

const calculusBcUnit9Notes: UnitNotes = {
  unitId: "unit-9",
  unitName: "Unit 9: Parametric Equations, Polar Coordinates, and Vector-Valued Functions",
  examWeight: "11–12%",
  focus: "Parametric, Polar, and Vector Analysis",
  sections: [
    {
      id: "unit-9-overview",
      title: "Overview",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            This unit transitions away from the standard Cartesian coordinate system (<InlineMath math="y=f(x)" />) where graphs must pass the vertical line test. We introduce three new modes of defining curves: <strong>Parametric</strong> (defined by a third variable <InlineMath math="t" />), <strong>Vector-Valued</strong> (magnitude and direction), and <strong>Polar</strong> (radius and angle).
          </p>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <p className="text-sm text-purple-800"><strong>BC Only:</strong> This entire unit is exclusive to AP Calculus BC.</p>
          </div>
        </div>
      )
    },
    {
      id: "topic-9-1",
      title: "Topic 9.1: Defining and Differentiating Parametric Equations",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Parametric Equations</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              In a parametric system, <InlineMath math="x" /> and <InlineMath math="y" /> are both defined as functions of a third independent variable, usually <InlineMath math="t" /> (time) or <InlineMath math="\theta" /> (angle).
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Equations:</strong> <InlineMath math="x = f(t)" /> and <InlineMath math="y = g(t)" /></li>
              <li><strong>Orientation:</strong> Unlike Cartesian graphs, parametric curves have <em>direction</em>. As <InlineMath math="t" /> increases, the curve is traced in a specific direction.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The First Derivative (Slope)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To find the slope <InlineMath math="\frac{dy}{dx}" /> at a specific point, we apply the Chain Rule:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="\frac{dy}{dx} = \frac{\frac{dy}{dt}}{\frac{dx}{dt}} \quad \text{provided } \frac{dx}{dt} \neq 0" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Tangent Lines</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Horizontal Tangents:</strong> Occur where <InlineMath math="\frac{dy}{dt} = 0" /> AND <InlineMath math="\frac{dx}{dt} \neq 0" /></li>
              <li><strong>Vertical Tangents:</strong> Occur where <InlineMath math="\frac{dx}{dt} = 0" /> AND <InlineMath math="\frac{dy}{dt} \neq 0" /></li>
              <li><strong>Indeterminate:</strong> If both equal 0, take the limit as <InlineMath math="t \to a" /></li>
            </ul>
            <ImportantNote>
              When asked for the equation of a tangent line, you need a point <InlineMath math="(x(t), y(t))" /> and a slope <InlineMath math="\frac{dy}{dx}" />. Evaluate at the specific <InlineMath math="t" />-value.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-2",
      title: "Topic 9.2: Second Derivatives of Parametric Equations",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Concept of Concavity</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The second derivative <InlineMath math="\frac{d^2y}{dx^2}" /> determines the concavity of the curve. Finding this in parametric mode requires careful application of the chain rule.
            </p>
            <ImportantNote>
              You <strong>cannot</strong> simply divide <InlineMath math="\frac{d^2y}{dt^2}" /> by <InlineMath math="\frac{d^2x}{dt^2}" />!
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Formula</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To find <InlineMath math="\frac{d^2y}{dx^2}" />, differentiate the first derivative with respect to <InlineMath math="t" /> and divide by <InlineMath math="\frac{dx}{dt}" />:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="\frac{d^2y}{dx^2} = \frac{\frac{d}{dt}\left(\frac{dy}{dx}\right)}{\frac{dx}{dt}}" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Procedure</h3>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700">
              <li>Find the first derivative <InlineMath math="\frac{dy}{dx}" /> as a function of <InlineMath math="t" /></li>
              <li>Take the derivative of that result with respect to <InlineMath math="t" /></li>
              <li>Divide that result by the original <InlineMath math="\frac{dx}{dt}" /></li>
            </ol>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-3",
      title: "Topic 9.3: Finding Arc Lengths of Curves (Parametric)",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Formula</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The length of a curve defined parametrically by <InlineMath math="x=f(t)" /> and <InlineMath math="y=g(t)" /> from <InlineMath math="t=a" /> to <InlineMath math="t=b" /> is derived from the Pythagorean Theorem:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="L = \int_{a}^{b} \sqrt{\left(\frac{dx}{dt}\right)^2 + \left(\frac{dy}{dt}\right)^2} \, dt" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Requirements</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>The curve must be traversed exactly once as <InlineMath math="t" /> goes from <InlineMath math="a" /> to <InlineMath math="b" /></li>
              <li><InlineMath math="f'(t)" /> and <InlineMath math="g'(t)" /> must be continuous on <InlineMath math="[a, b]" /></li>
            </ul>
            <ImportantNote>
              Most arc length integrals on the AP exam are difficult to solve by hand. They usually appear in the Calculator Active section or ask you only to "Write, but do not evaluate, an integral."
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-4",
      title: "Topic 9.4: Defining and Differentiating Vector-Valued Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Vector-Valued Functions</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A vector-valued function describes the position of a particle in a plane using a vector <InlineMath math="\vec{r}(t)" />.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <p className="text-slate-700"><strong>Notation:</strong> <InlineMath math="\vec{r}(t) = \langle x(t), y(t) \rangle = x(t)\mathbf{i} + y(t)\mathbf{j}" /></p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Differentiation</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Differentiation is performed component-wise. If <InlineMath math="\vec{r}(t)" /> is position:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Velocity Vector:</strong> <InlineMath math="\vec{v}(t) = \vec{r}'(t) = \langle x'(t), y'(t) \rangle" /></li>
              <li><strong>Acceleration Vector:</strong> <InlineMath math="\vec{a}(t) = \vec{v}'(t) = \langle x''(t), y''(t) \rangle" /></li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Speed vs. Velocity</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Velocity</strong> is a vector (has magnitude and direction)</li>
              <li><strong>Speed</strong> is a scalar (magnitude only)</li>
            </ul>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mt-4">
              <BlockMath math="\text{Speed} = \|\vec{v}(t)\| = \sqrt{(x'(t))^2 + (y'(t))^2}" />
            </div>
            <p className="text-slate-700 mt-4">
              Note: The formula for speed is the integrand of the Arc Length formula.
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-5",
      title: "Topic 9.5: Integrating Vector-Valued Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Indefinite Integrals</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To integrate a vector function, integrate each component separately:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="\int \langle f(t), g(t) \rangle \, dt = \left\langle \int f(t)dt, \int g(t)dt \right\rangle + \vec{C}" />
            </div>
            <ImportantNote>
              Don't forget the constant of integration vector <InlineMath math="\vec{C} = \langle C_1, C_2 \rangle" />.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Definite Integrals (Displacement)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The definite integral of velocity yields the <strong>displacement</strong> (net change in position), not the total distance traveled.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <BlockMath math="\text{Displacement} = \int_{a}^{b} \vec{v}(t) \, dt = \langle x(b)-x(a), y(b)-y(a) \rangle" />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-6",
      title: "Topic 9.6: Solving Motion Problems (Parametric/Vector)",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Determining Position</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If given the velocity vector <InlineMath math="\vec{v}(t)" /> and an initial position at <InlineMath math="t=a" />:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="\text{Position}(b) = \text{Position}(a) + \int_{a}^{b} \vec{v}(t) \, dt" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Total Distance Traveled</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Total distance is the integral of speed (arc length):
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="\text{Total Distance} = \int_{a}^{b} \|\vec{v}(t)\| \, dt = \int_{a}^{b} \sqrt{(x'(t))^2 + (y'(t))^2} \, dt" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Particle Behavior</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>At Rest:</strong> <InlineMath math="\vec{v}(t) = \langle 0, 0 \rangle" />. Both <InlineMath math="x'(t)" /> and <InlineMath math="y'(t)" /> must be zero.</li>
              <li><strong>Vertical Motion:</strong> <InlineMath math="x'(t) = 0" /></li>
              <li><strong>Horizontal Motion:</strong> <InlineMath math="y'(t) = 0" /></li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-7",
      title: "Topic 9.7: Defining Polar Coordinates and Differentiating",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Polar vs. Rectangular</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Rectangular:</strong> <InlineMath math="(x, y)" /> - location based on horizontal/vertical distance</li>
              <li><strong>Polar:</strong> <InlineMath math="(r, \theta)" /> - location based on distance from origin (pole) <InlineMath math="r" />, and angle from positive x-axis <InlineMath math="\theta" /></li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Conversion Formulas</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <ul className="list-disc pl-5 space-y-1 text-slate-700">
                <li><InlineMath math="x = r \cos\theta" /></li>
                <li><InlineMath math="y = r \sin\theta" /></li>
                <li><InlineMath math="x^2 + y^2 = r^2" /></li>
                <li><InlineMath math="\tan\theta = \frac{y}{x}" /></li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Derivative (Slope)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Curves are usually defined as <InlineMath math="r = f(\theta)" />. To find the slope <InlineMath math="\frac{dy}{dx}" />, express <InlineMath math="x" /> and <InlineMath math="y" /> parametrically in terms of <InlineMath math="\theta" />:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="\frac{dy}{dx} = \frac{\frac{dy}{d\theta}}{\frac{dx}{d\theta}} = \frac{r'\sin\theta + r\cos\theta}{r'\cos\theta - r\sin\theta}" />
            </div>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><InlineMath math="\frac{dy}{dx}" /> is the slope of the curve in the <InlineMath math="xy" />-plane</li>
              <li><InlineMath math="\frac{dr}{d\theta}" /> is the rate of change of the radius with respect to the angle</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-8",
      title: "Topic 9.8: Area of a Polar Region",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Concept</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              In polar coordinates, we sum the area of infinite <strong>sectors</strong> of a circle. The area of a circle sector is <InlineMath math="A = \frac{1}{2}r^2\theta" />.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Formula</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="A = \frac{1}{2} \int_{\alpha}^{\beta} (r(\theta))^2 \, d\theta" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Determining Bounds</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Finding <InlineMath math="\alpha" /> and <InlineMath math="\beta" /> is often the hardest part:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>Set <InlineMath math="r=0" /> to find where the curve passes through the pole (often creates bounds for "petals" of rose curves)</li>
              <li>Use algebraic symmetry to simplify (e.g., integrate <InlineMath math="0" /> to <InlineMath math="\pi" /> and multiply by 2)</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-9",
      title: "Topic 9.9: Area Bounded by Two Polar Curves",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Area Between Curves</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If finding the area between an "outer" curve <InlineMath math="R(\theta)" /> and an "inner" curve <InlineMath math="r(\theta)" />:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="A = \frac{1}{2} \int_{\alpha}^{\beta} \left( (R(\theta))^2 - (r(\theta))^2 \right) \, d\theta" />
            </div>
            <ImportantNote>
              <strong>Common Mistake:</strong> Students often write <InlineMath math="(R - r)^2" />. This is incorrect. You must square the radii individually: <InlineMath math="R^2 - r^2" />.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Finding Intersections</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To find bounds <InlineMath math="\alpha" /> and <InlineMath math="\beta" />, set the two equations equal (<InlineMath math="R(\theta) = r(\theta)" />) and solve for <InlineMath math="\theta" />.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Split Integrals</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Sometimes the boundary changes from one curve to the other at an intersection. In this case, split the integral at the intersection point.
            </p>
            <ImportantNote>
              Always sketch the graph or use a calculator to identify which curve is "outer" (further from the origin) and where the intersections lie.
            </ImportantNote>
          </section>
        </div>
      )
    }
  ]
};

export default calculusBcUnit9Notes;
